/* eslint-disable array-callback-return */
import { useEffect, useState } from "react";
import { SchoolLicenseWithAddtitionalLicense } from "../api/school-license";
import { isEmpty } from "lodash";
import { SupportedLanguage } from "./../components/IntlHandler/IntlHelper";
import { FrenchInstructorRole, Role } from "../types/Role";
import { TeacherVerificationMetadataStatus } from "../types/User";

/**
 * Based on https://gist.github.com/javilobo8/097c30a233786be52070986d8cdb1743
 * @returns function to revoke download url
 */
export const downloadFile = (
  filename: string,
  downloadUrl: string
): (() => void) => {
  const link = document.createElement("a");
  link.href = downloadUrl;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();

  return () => {
    URL.revokeObjectURL(downloadUrl);
  };
};

/**
 * Returns a string like YYYYMMDDHHmmss
 */
export const dateToString = (date: Date): string => {
  const year = date.getFullYear().toString().padStart(4, "0");
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const day = date.getDate().toString().padStart(2, "0");
  const hours = date.getHours().toString().padStart(2, "0");
  const minutes = date.getMinutes().toString().padStart(2, "0");
  const seconds = date.getSeconds().toString().padStart(2, "0");

  return `${year}${month}${day}${hours}${minutes}${seconds}`;
};

export const reverseSpecifiedFormatToDate = (
  date: string | undefined,
  format?: "dd.mm.yyyy" | "dd/mm/yyyy" | "dd-mm-yyyy" | "yyyy-mm-dd"
) => {
  const regex = /^\d{2}\.\d{2}\.\d{4}$/; // check type dd.mm.yyyy
  if (date) {
    if (regex.test(date)) {
      // test valid format dd.mm.yyyy
      const parts: string[] = date.split(".");

      if (parts.length === 3) {
        const day = Number(parts[0]);
        const month: number = Number(parts[1]) - 1;
        const year = Number(parts[2]);

        return new Date(year, month, day);
      } else {
        return;
      }
    } else {
      return new Date(date);
    }
  }
};

export const formatUTCDate = (
  inputDate: string,
  format?: "dd.mm.yyyy" | "dd/mm/yyyy" | "dd-mm-yyyy" | "yyyy-mm-dd"
): string | undefined => {
  if (inputDate && inputDate.toString() === "Invalid Date") {
    return;
  }
  const dateObject = new Date(inputDate);

  const hours = String(dateObject.getUTCHours()).padStart(2, "0");
  const minutes = String(dateObject.getUTCMinutes()).padStart(2, "0");
  const seconds = String(dateObject.getUTCSeconds()).padStart(2, "0");

  const day = String(dateObject.getUTCDate()).padStart(2, "0");
  const month = String(dateObject.getUTCMonth() + 1).padStart(2, "0"); // Month is 0-indexed
  const year = dateObject.getUTCFullYear();

  if (format === "dd.mm.yyyy") {
    return `${day}.${month}.${year}`;
  }

  if (format === "dd/mm/yyyy") {
    return `${day}/${month}/${year}`;
  }

  if (format === "dd-mm-yyyy") {
    return `${day}-${month}-${year}`;
  }

  if (format === "yyyy-mm-dd") {
    return `${year}-${month}-${day}`;
  }

  // default: hh:mm:ss - dd/mm/yyyy
  const formattedDate = `${hours}:${minutes}:${seconds} - ${day}/${month}/${year}`;

  return formattedDate;
};

export const arrayToFilename = (array: any[]) => {
  return array
    .toString()
    .toLowerCase()
    .replace("[", "")
    .replace("]", "")
    .replace(",", "_")
    .replace(" ", "");
};

const getSessionStorage = (key: string, defaultValue: string) => {
  const stored = sessionStorage.getItem(key);
  if (!stored) {
    return defaultValue;
  }
  return JSON.parse(stored);
};

export const useSessionStorage = (key: string, defaultValue: string) => {
  const [value, setValue] = useState(getSessionStorage(key, defaultValue));
  useEffect(() => {
    sessionStorage.setItem(key, JSON.stringify(value));
  }, [key, value]);

  return [value, setValue];
};

/**
 * Groups all items in an array of objects `T` where the value of property `K` is the same
 * Note that equality is checked by comparing the stringified versions of property `K`.
 * @param array Items to group
 * @param key Key of `T` to group by
 */
export function groupBy<T, K extends keyof T>(array: T[], key: K) {
  const stringRepresentation = (obj: any): string => {
    let stringRep = obj.toString();
    if (Array.isArray(obj)) {
      obj.sort();
      stringRep = obj.join(":");
    }
    return stringRep;
  };

  const map = new Map<string, T[]>();
  array.forEach((item) => {
    const stringKey = stringRepresentation(item[key]);
    if (!map.has(stringKey)) {
      map.set(
        stringKey,
        array.filter((i) => stringRepresentation(i[key]) === stringKey)
      );
    }
  });
  return map;
}

export function replaceAllStrings(text: string, replaceMap: any) {
  const reg = new RegExp(Object.keys(replaceMap).join("|"), "gi");
  return text.replace(reg, function (matched) {
    return replaceMap[matched.toUpperCase()];
  });
}

// Fixxxxxx
export const validateSchoolLicense = (
  schoolLicenses: SchoolLicenseWithAddtitionalLicense[]
): boolean => {
  let isSchoolLicenseValid: boolean = false;

  if (schoolLicenses && !isEmpty(schoolLicenses)) {
    for (let schoolLicense of schoolLicenses) {
      if (
        new Date().getTime() - new Date(schoolLicense.validUntil).getTime() <
        0
      ) {
        isSchoolLicenseValid = true;
      }
    }
  }
  return isSchoolLicenseValid;
};

export const formatSchoolLicenses = (
  licenseProducts: SchoolLicenseWithAddtitionalLicense[]
) => {
  let learning = [];
  let plus = [];

  for (let item of licenseProducts) {
    if (item.products[0] === "CLASSPAD_LEARNING") {
      learning.push(item);
    }

    if (item.products[0] === "CLASSPAD_NET_PLUS") {
      plus.push(item);
    }
  }
  learning.sort((a, b) => {
    return new Date(b.validUntil).valueOf() - new Date(a.validUntil).valueOf();
  });

  plus.sort((a, b) => {
    return new Date(b.validUntil).valueOf() - new Date(a.validUntil).valueOf();
  });

  const lastestCPLearing = learning[0];
  const lastestCPPlus = plus[0];

  let result: SchoolLicenseWithAddtitionalLicense[] = [];

  if (lastestCPLearing && !lastestCPPlus) {
    result = [lastestCPLearing];
  }
  if (!lastestCPLearing && lastestCPPlus) {
    result = [lastestCPPlus];
  }
  if (lastestCPLearing && lastestCPPlus) {
    result = [lastestCPLearing, lastestCPPlus];
  }

  return result;
};

export function buildSearchParams(paramsObject: { [key: string]: any }) {
  const params = [];

  for (const key in paramsObject) {
    if (
      paramsObject.hasOwnProperty(key) &&
      paramsObject[key] !== undefined &&
      paramsObject[key] !== null
    ) {
      params.push(`${key}=${encodeURIComponent(paramsObject[key])}`);
    }
  }

  return params.length > 0 ? `&${params.join("&")}` : "";
}

export function convertStatusToText(status: boolean | undefined): string {
  if (typeof status !== "boolean") {
    return "";
  }

  return status ? "Activated" : "Non Activated";
}

export const dateWithoutTimezone = (date: Date) => {
  const tzoffset = date.getTimezoneOffset() * 60000; //offset in milliseconds
  const withoutTimezone = new Date(date.valueOf() - tzoffset)
    .toISOString()
    .slice(0, -1);
  return withoutTimezone;
};

export const capitalizeFirstLetter = (string: string) => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};

export const getTeachingLevelSelectValue = (
  value: string[]
): { value: string; label: string }[] => {
  const options = getTypesTeachingLevelAdmin("fr");

  const result = options.filter((option) => {
    if (value.includes(option.value)) return option;
  });

  return result;
};

interface SchoolType {
  readonly value: string;
  readonly label: string;
}

export const getTypesTeachingLevelAdmin = (
  language: SupportedLanguage
): SchoolType[] => {
  const types: any = getAllTypesTeachingLevelAdmin();
  return types[language];
};

const getAllTypesTeachingLevelAdmin = (): {
  de: {
    value: string;
    label: string;
  }[];
  fr: {
    value: string;
    label: string;
  }[];
  en: {
    value: string;
    label: string;
  }[];
} => {
  const germanSchoolTypes = [
    {
      value: "primary_level",
      label: "Primary",
    },
    {
      value: "secondary_school",
      label: "College",
    },
    {
      value: "secondary_school_2",
      label: "High School",
    },
    {
      value: "tutoring_school",
      label: "Tutoring",
    },
    {
      value: "second_chance_education",
      label: "Second-chance education",
    },
    {
      value: "professional_school",
      label: "professional school",
    },
    {
      value: "university",
      label: "Superior",
    },
    { value: "other", label: "Other" },
  ];

  const frenchSchoolTypes = [
    {
      value: "primary_level",
      label: "Primary",
    },
    {
      value: "secondary_school",
      label: "College",
    },
    {
      value: "secondary_school_2",
      label: "High School",
    },
    {
      value: "university",
      label: "Superior",
    },
    { value: "segpa", label: "SEGPA" },
    { value: "erea", label: "EREA" },
    { value: "mfr", label: "MFR" },
    { value: "inspe", label: "INSPE" },
    { value: "cfa", label: "CFA" },
    { value: "bts", label: "BTS" },
  ];

  const schoolTypes = {
    de: germanSchoolTypes,
    fr: frenchSchoolTypes,
    en: germanSchoolTypes,
  };
  return schoolTypes;
};

export const convertFileSizeKBToMB = (fileSize: number) => {
  return (fileSize / (1024 * 1024)).toFixed(2);
};

export const MAX_TOTAL_SIZE_MB = 25;

export const camelCaseToWords = (s: string) => {
  const result = s.replace(/([A-Z])/g, " $1");
  return result.charAt(0).toUpperCase() + result.slice(1);
};

export const checkEmailDTCIsWhiteList = (email: string): boolean => {
  const whitelist = [
    "@casio.fr",
    "@educagri.fr",
    "@cneap.fr",
    "@mfr.asso.fr",
    "@ac-aix-marseille.fr",
    "@ac-amiens.fr",
    "@ac-besancon.fr",
    "@ac-bordeaux.fr",
    "@ac-caen.fr",
    "@ac-clermont.fr",
    "@ac-corse.fr",
    "@ac-creteil.fr",
    "@ac-dijon.fr",
    "@ac-grenoble.fr",
    "@ac-guadeloupe.fr",
    "@ac-guyane.fr",
    "@ac-lille.fr",
    "@ac-limoges.fr",
    "@ac-lyon.fr",
    "@ac-martinique.fr",
    "@ac-mayotte.fr",
    "@ac-montpellier.fr",
    "@ac-nancy-metz.fr",
    "@ac-nantes.fr",
    "@ac-nice.fr",
    "@ac-noumea.nc",
    "@ac-polynesie.pf",
    "@ac-wf.wf",
    "@ac-orleans-tours.fr",
    "@ac-paris.fr",
    "@ac-poitiers.fr",
    "@ac-reims.fr",
    "@ac-rennes.fr",
    "@ac-reunion.fr",
    "@ac-rouen.fr",
    "@ac-strasbourg.fr",
    "@ac-toulouse.fr",
    "@ac-versailles.fr",
    "@ac-normandie.fr",
    "@ccca-btp.fr",
    "@cfa.fr",
    "@btpcfa-iledefrance.fr",
    "@btpcfa-grandest.fr",
    "@btpcfa-occitanie.fr",
    "@btpcfa-poitou-charentes.fr",
    "@btpcfa-cvdl.fr",
    "@btp-cfa-paca.fr",
    "@btpcfa-paca.fr",
    "@btpcfa-aura.fr",
    "@btpcfa-aquitaine.fr",
    "@btpcfa-somme.fr",
    "@btpcfa-pdl.com",
    "@btpcfa-pdl.fr",
    "@btpcfa-picardie.fr",
    "@btpcfa-oise.fr",
    "@btpcfa-hautsdefrance.fr",
    "@cfa-btp-toulouse.fr",
    "@aefe.fr",
  ];

  const domain = email.substring(email.lastIndexOf("@"));

  return whitelist.includes(domain);
};

export const convertFrenchInstructorRole = (
  role: FrenchInstructorRole | undefined
): string => {
  let result: string = "";
  switch (role) {
    case undefined:
      result = "Teacher";
      break;
    case FrenchInstructorRole.INSTRUCTOR:
      result = "Teacher";
      break;
    case FrenchInstructorRole.SCHOOL_ADMIN:
      result = "School Admin";
      break;
    case FrenchInstructorRole.SCHOOL_ASSOCIATION:
      result = "School Association";
      break;
    case FrenchInstructorRole.OTHER:
      result = "Other";
      break;
  }
  return result;
};

export const convertTeacherVerificationStatus = (
  status: TeacherVerificationMetadataStatus | undefined
): string => {
  let result: string = "";
  switch (status) {
    case undefined:
      result = "Null";
      break;
    case TeacherVerificationMetadataStatus.APPROVED:
      result = "Approved";
      break;
    case TeacherVerificationMetadataStatus.PENDING:
      result = "Pending";
      break;
    case TeacherVerificationMetadataStatus.REJECTED:
      result = "Rejected";
      break;
    case TeacherVerificationMetadataStatus.NOT_SUBMITTED:
      result = "Not Submitted";
      break;
  }

  return result;
};

export const convertAdminUserRole = (role: Role): string => {
  let result: string = "";
  switch (role) {
    case Role.SUPER_ADMIN:
      result = "Super Admin";
      break;
    case Role.ADMINISTRATOR:
      result = "Admin";
      break;
    case Role.SUPPORT:
      result = "Support Staff";
      break;
  }
  return result;
};
